import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { camerasAPI, zonesAPI, detectProfilesAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useLiveStreamStore } from '../stores/liveStreamStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ZoneAssignmentModal from '../components/zones/ZoneAssignmentModal';
import {
  ArrowLeftIcon,
  VideoCameraIcon,
  Cog6ToothIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  CameraIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  TruckIcon,
  ClockIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  BeakerIcon,
  DocumentCheckIcon,
  FireIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

// Default confidence thresholds
const DEFAULT_CONFIDENCE_THRESHOLDS = {
  person: 0.5,
  ppe: 0.6,
  fire: 0.5,
  vehicle: 0.7,
};

// Default vehicle proximity settings
const DEFAULT_VEHICLE_PROXIMITY = {
  enabled: false,
  confidence: 0.7,
  minSizeRatio: 0.02,  // 2% of frame
  cooldown: 30,  // seconds
  // Distance thresholds in meters
  criticalDistance: 2.0,  // < 2m - immediate danger
  highDistance: 5.0,      // 2-5m - dangerous proximity
  mediumDistance: 10.0,   // 5-10m - warning zone
};

// Default processing settings
const DEFAULT_PROCESSING_SETTINGS = {
  targetFps: 5,           // Target frames per second for processing
  priority: 'normal',     // Camera priority: critical, high, normal, low
};

// Helper function to extract PPE confidence from either old or new format
function extractPPEConfidence(ppeValue, defaultValue = 0.6) {
  // If it's already a number (old format), return it
  if (typeof ppeValue === 'number') {
    return ppeValue;
  }

  // If it's an object (new format with worn/violation), extract a single value
  if (ppeValue && typeof ppeValue === 'object') {
    const worn = ppeValue.worn;
    if (worn && typeof worn === 'object') {
      // Use vest threshold if available (most sensitive)
      if (worn.vest && typeof worn.vest === 'number') {
        return worn.vest;
      }
      // Otherwise, average all worn thresholds
      const wornValues = Object.values(worn).filter(v => typeof v === 'number');
      if (wornValues.length > 0) {
        return wornValues.reduce((a, b) => a + b, 0) / wornValues.length;
      }
    }
  }

  // Fallback to default
  return defaultValue;
}

// Priority options
const PRIORITY_OPTIONS = [
  { id: 'critical', name: 'Critical', description: 'Processed first, highest frame rate', color: 'red' },
  { id: 'high', name: 'High', description: 'High priority processing', color: 'orange' },
  { id: 'normal', name: 'Normal', description: 'Standard processing priority', color: 'blue' },
  { id: 'low', name: 'Low', description: 'Background processing, saves resources', color: 'gray' },
];

// Available analytics types - matches AI engine PPE model capabilities
const ANALYTICS_OPTIONS = [
  // ===== CRITICAL SAFETY =====
  {
    id: 'fire_detection',
    name: 'Fire Detection',
    description: 'Detect fire and flames in the scene',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    category: 'fire',
  },
  {
    id: 'smoke_detection',
    name: 'Smoke Detection',
    description: 'Detect smoke that may indicate fire hazard',
    icon: ExclamationTriangleIcon,
    severity: 'HIGH',
    category: 'fire',
  },
  {
    id: 'fall_detection',
    name: 'Fall Detection',
    description: 'Detect when a person falls down',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    category: 'behavior',
  },
  {
    id: 'trespassing',
    name: 'Trespassing Detection',
    description: 'Alert when unauthorized person enters restricted area',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'zone',
  },
  // ===== PPE DETECTION =====
  {
    id: 'ppe_hardhat',
    name: 'Hard Hat Detection',
    description: 'Detect missing hard hats (head_nohelmet)',
    icon: ShieldCheckIcon,
    severity: 'HIGH',
    category: 'ppe',
  },
  {
    id: 'ppe_vest',
    name: 'Safety Vest Detection',
    description: 'Detect missing safety vests',
    icon: ShieldCheckIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_glasses',
    name: 'Safety Glasses Detection',
    description: 'Detect missing safety glasses (no_glasses)',
    icon: EyeIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_gloves',
    name: 'Gloves Detection',
    description: 'Detect missing gloves (no_gloves)',
    icon: ShieldCheckIcon,
    severity: 'LOW',
    category: 'ppe',
  },
  {
    id: 'ppe_mask',
    name: 'Face Mask Detection',
    description: 'Detect missing face masks (no_mask)',
    icon: ShieldCheckIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_boots',
    name: 'Safety Boots Detection',
    description: 'Detect improper footwear (barefoot/sandals)',
    icon: ShieldCheckIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_ear_protection',
    name: 'Ear Protection Detection',
    description: 'Detect missing ear protection',
    icon: ShieldCheckIcon,
    severity: 'LOW',
    category: 'ppe',
  },
  {
    id: 'ppe_harness',
    name: 'Safety Harness Detection',
    description: 'Detect safety harness presence',
    icon: ShieldCheckIcon,
    severity: 'HIGH',
    category: 'ppe',
  },
  {
    id: 'zone_breach',
    name: 'Zone Breach Detection',
    description: 'Alert when someone enters a restricted zone',
    icon: MapPinIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'zone',
  },
  {
    id: 'crowd_detection',
    name: 'Crowd Detection',
    description: 'Detect overcrowding in an area',
    icon: UserIcon,
    severity: 'HIGH',
    category: 'behavior',
  },
  {
    id: 'vehicle_proximity',
    name: 'Vehicle Proximity Detection',
    description: 'Alert when person is dangerously close to vehicles',
    icon: TruckIcon,
    severity: 'CRITICAL',
    category: 'vehicle',
    hasAdvancedSettings: true,
  },
  // ===== EQUIPMENT & VEHICLE INTELLIGENCE =====
  {
    id: 'equipment_exclusion',
    name: 'Equipment Exclusion Zone',
    description: 'Alert when person enters heavy equipment exclusion zone',
    icon: NoSymbolIcon,
    severity: 'CRITICAL',
    category: 'equipment',
  },
  {
    id: 'vehicle_speed',
    name: 'Vehicle Speed Estimation',
    description: 'Detect vehicles exceeding speed limits',
    icon: TruckIcon,
    severity: 'HIGH',
    category: 'vehicle',
  },
  {
    id: 'license_plate',
    name: 'License Plate Recognition',
    description: 'Read and verify vehicle license plates',
    icon: TruckIcon,
    severity: 'HIGH',
    category: 'vehicle',
  },
  {
    id: 'idle_detection',
    name: 'Equipment Idle Detection',
    description: 'Detect equipment idle beyond time threshold',
    icon: ClockIcon,
    severity: 'MEDIUM',
    category: 'equipment',
  },
  // ===== ENVIRONMENTAL & HOUSEKEEPING =====
  {
    id: 'debris_detection',
    name: 'Housekeeping / Debris Detection',
    description: 'Detect scattered tools, materials, and clutter',
    icon: WrenchScrewdriverIcon,
    severity: 'MEDIUM',
    category: 'housekeeping',
  },
  {
    id: 'spill_detection',
    name: 'Spill Detection',
    description: 'Detect liquid and chemical spills',
    icon: BeakerIcon,
    severity: 'HIGH',
    category: 'environmental',
  },
  {
    id: 'barricade_detection',
    name: 'Barricade / Signage Compliance',
    description: 'Verify barricades, cones, and warning signs are in place',
    icon: ShieldCheckIcon,
    severity: 'HIGH',
    category: 'compliance',
  },
  {
    id: 'exit_obstruction',
    name: 'Blocked Emergency Exit',
    description: 'Detect when emergency exits are obstructed',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'compliance',
  },
  // ===== PROCESS & PERMIT COMPLIANCE =====
  {
    id: 'hotwork_detection',
    name: 'Hot Work Fire Watch',
    description: 'Ensure fire watch personnel present during hot work',
    icon: FireIcon,
    severity: 'CRITICAL',
    category: 'permit',
  },
  {
    id: 'confined_space',
    name: 'Confined Space Attendant',
    description: 'Monitor confined space entry and attendant presence',
    icon: DocumentCheckIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'permit',
  },
  {
    id: 'scaffolding_detection',
    name: 'Scaffolding Safety',
    description: 'Detect scaffolding components: guardrails, toe boards',
    icon: WrenchScrewdriverIcon,
    severity: 'HIGH',
    category: 'compliance',
  },
  // ===== BEHAVIORAL SAFETY =====
  {
    id: 'lifting_detection',
    name: 'Improper Lifting Detection',
    description: 'Detect unsafe lifting posture using pose analysis',
    icon: ExclamationTriangleIcon,
    severity: 'HIGH',
    category: 'behavior',
  },
  {
    id: 'phone_usage',
    name: 'Phone Usage Detection',
    description: 'Detect phone usage in hazard zones (pose-based)',
    icon: UserIcon,
    severity: 'MEDIUM',
    category: 'behavior',
  },
  {
    id: 'smoking_detection',
    name: 'Smoking in Prohibited Areas',
    description: 'Detect smoking violations in restricted zones',
    icon: FireIcon,
    severity: 'HIGH',
    category: 'behavior',
  },
  {
    id: 'fatigue_detection',
    name: 'Worker Fatigue Detection',
    description: 'Detect drowsiness and low activity patterns',
    icon: ClockIcon,
    severity: 'MEDIUM',
    category: 'behavior',
  },
  {
    id: 'height_posture',
    name: 'Unsafe Posture at Height',
    description: 'Detect leaning/unsafe posture in height work zones',
    icon: ExclamationTriangleIcon,
    severity: 'HIGH',
    requiresZones: true,
    category: 'behavior',
  },
  {
    id: 'harness_enforcement',
    name: 'Context-Aware Harness Check',
    description: 'Enforce harness wear in height/scaffolding zones',
    icon: ShieldCheckIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'ppe',
  },
];

// Zone types
const ZONE_TYPES = [
  { id: 'restricted', name: 'Restricted Zone', color: '#EF4444' },
  { id: 'hazard', name: 'Hazard Zone', color: '#F97316' },
  { id: 'general', name: 'General Zone', color: '#3B82F6' },
  { id: 'entry', name: 'Entry Zone', color: '#10B981' },
  { id: 'exit', name: 'Exit Zone', color: '#8B5CF6' },
  { id: 'confined_space', name: 'Confined Space', color: '#EC4899' },
  { id: 'crane_zone', name: 'Crane Operating Zone', color: '#F59E0B' },
  { id: 'excavation', name: 'Excavation Zone', color: '#92400E' },
  { id: 'chemical', name: 'Chemical Storage Zone', color: '#7C3AED' },
  { id: 'electrical', name: 'Electrical Work Zone', color: '#FBBF24' },
  { id: 'hot_work', name: 'Hot Work Zone', color: '#DC2626' },
  { id: 'height_work', name: 'Height Work / Scaffolding', color: '#059669' },
];

// Zone selector dropdown component
function ZoneSelector({ zones, selectedZones, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAllSelected = selectedZones.includes('all') || selectedZones.length === 0;

  const handleToggleAll = () => {
    if (isAllSelected) {
      // If currently all, and we have zones, select first zone
      if (zones.length > 0) {
        onChange([zones[0].id]);
      }
    } else {
      onChange(['all']);
    }
  };

  const handleToggleZone = (zoneId) => {
    if (selectedZones.includes('all')) {
      // Switching from "all" to specific zone
      onChange([zoneId]);
    } else if (selectedZones.includes(zoneId)) {
      // Remove zone
      const newSelection = selectedZones.filter(id => id !== zoneId);
      // If no zones selected, default to all
      onChange(newSelection.length > 0 ? newSelection : ['all']);
    } else {
      // Add zone
      onChange([...selectedZones, zoneId]);
    }
  };

  const getDisplayText = () => {
    if (isAllSelected) return 'Entire Frame';
    if (selectedZones.length === 1) {
      const zone = zones.find(z => z.id === selectedZones[0]);
      return zone?.name || 'Selected Zone';
    }
    return `${selectedZones.length} zones`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg',
          disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white hover:bg-gray-50 text-gray-700'
        )}
      >
        <MapPinIcon className="w-4 h-4" />
        <span>{getDisplayText()}</span>
        <ChevronDownIcon className="w-3 h-3" />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-56 bg-white border rounded-lg shadow-lg py-1">
          {/* Entire Frame option */}
          <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleToggleAll}
              className="rounded text-blue-600"
            />
            <span className="text-sm font-medium">Entire Frame</span>
          </label>

          {zones.length > 0 && (
            <>
              <div className="border-t my-1" />
              <div className="px-3 py-1 text-xs text-gray-500 font-medium">
                Specific Zones
              </div>
              {zones.map(zone => {
                const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
                return (
                  <label
                    key={zone.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={!isAllSelected && selectedZones.includes(zone.id)}
                      onChange={() => handleToggleZone(zone.id)}
                      className="rounded text-blue-600"
                    />
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: zoneType?.color }}
                    />
                    <span className="text-sm">{zone.name}</span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function CameraConfigPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedSite } = useSiteStore();

  // State - new structure with analytics containing zone assignments
  const [analytics, setAnalytics] = useState(
    ANALYTICS_OPTIONS.map(opt => ({
      id: opt.id,
      enabled: false,
      zones: ['all'], // Default to entire frame
    }))
  );
  const [zones, setZones] = useState([]);
  const [confidenceThresholds, setConfidenceThresholds] = useState(DEFAULT_CONFIDENCE_THRESHOLDS);
  const [vehicleProximitySettings, setVehicleProximitySettings] = useState(DEFAULT_VEHICLE_PROXIMITY);
  const [processingSettings, setProcessingSettings] = useState(DEFAULT_PROCESSING_SETTINGS);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [selectedZoneType, setSelectedZoneType] = useState('restricted');
  const [newZoneName, setNewZoneName] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showZoneAssignmentModal, setShowZoneAssignmentModal] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  // Memoize the onDrawingModeChange callback to prevent infinite loops
  const handleDrawingModeChange = useCallback((enabled) => {
    setIsDrawing(enabled);
    if (!enabled) {
      setCurrentPoints([]);
      setNewZoneName('');
    }
  }, []);

  // Reference image state
  const [referenceImage, setReferenceImage] = useState(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [snapshotError, setSnapshotError] = useState(null);

  // Live stream store for snapshot requests
  const { requestSnapshot, activeStreams, connected, connect } = useLiveStreamStore();
  const streamState = activeStreams[id];

  // Canvas ref
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const hasRequestedSnapshot = useRef(false);

  // Fetch camera data
  const { data, isLoading } = useQuery({
    queryKey: ['camera', id],
    queryFn: () => camerasAPI.getCamera(id),
  });

  const camera = data?.data?.data;

  // Fetch available detection profiles
  const { data: profilesData } = useQuery({
    queryKey: ['detection-profiles'],
    queryFn: async () => {
      const response = await detectProfilesAPI.getProfiles();
      return response.data?.profiles || [];
    },
  });

  const profiles = profilesData || [];

  // Fetch available zones for this site from database
  const { data: zonesData } = useQuery({
    queryKey: ['zones', selectedSite?.id],
    queryFn: async () => {
      const response = await zonesAPI.getZones({ siteId: selectedSite?.id });
      return response.data || [];
    },
    enabled: !!selectedSite?.id,
  });

  const availableZones = Array.isArray(zonesData) ? zonesData : [];

  // Load saved config when camera data loads
  useEffect(() => {
    console.log('🔄 useEffect triggered - Loading camera config:', {
      cameraId: camera?.id,
      cameraExists: !!camera,
      detectionProfileId: camera?.detectionProfileId,
      detectionProfileIdType: typeof camera?.detectionProfileId,
      hasDetectionProfile: !!camera?.detectionProfile,
      cameraKeys: camera ? Object.keys(camera) : [],
    });

    // Initialize with profile ID if camera has one assigned
    if (camera && camera.detectionProfileId) {
      console.log('✅ SETTING selectedProfileId to:', camera.detectionProfileId);
      setSelectedProfileId(camera.detectionProfileId);
    } else {
      console.log('⚠️ Condition failed:', {
        cameraExists: !!camera,
        hasDetectionProfileId: !!camera?.detectionProfileId,
        detectionProfileId: camera?.detectionProfileId,
      });
      setSelectedProfileId(null);
    }

    // Step 1: Load base config from detection profile if assigned
    if (camera?.detectionProfile?.config) {
      const profileConfig = camera.detectionProfile.config;

      // Load confidence thresholds from profile as base
      if (profileConfig.confidence_thresholds) {
        // Normalize PPE threshold: handle both old (number) and new (object) formats
        const normalizedThresholds = {
          ...profileConfig.confidence_thresholds,
          ppe: extractPPEConfidence(profileConfig.confidence_thresholds.ppe, 0.6),
        };
        setConfidenceThresholds(normalizedThresholds);
      } else {
        setConfidenceThresholds(DEFAULT_CONFIDENCE_THRESHOLDS);
      }

      // Load vehicle proximity from profile
      if (profileConfig.vehicleProximity) {
        setVehicleProximitySettings({
          ...DEFAULT_VEHICLE_PROXIMITY,
          ...profileConfig.vehicleProximity,
        });
      } else {
        setVehicleProximitySettings(DEFAULT_VEHICLE_PROXIMITY);
      }

      // Load processing settings from profile
      if (profileConfig.processing) {
        setProcessingSettings({
          ...DEFAULT_PROCESSING_SETTINGS,
          ...profileConfig.processing,
        });
      } else {
        setProcessingSettings(DEFAULT_PROCESSING_SETTINGS);
      }
    } else {
      // No profile assigned, use defaults
      setConfidenceThresholds(DEFAULT_CONFIDENCE_THRESHOLDS);
      setVehicleProximitySettings(DEFAULT_VEHICLE_PROXIMITY);
      setProcessingSettings(DEFAULT_PROCESSING_SETTINGS);
    }

    // Step 2: Merge with camera-specific overrides (detectionConfig takes precedence)
    if (camera?.detectionConfig) {
      const config = typeof camera.detectionConfig === 'string'
        ? JSON.parse(camera.detectionConfig)
        : camera.detectionConfig;

      // Merge confidence thresholds: overrides take precedence
      if (config.confidence_thresholds) {
        // Normalize PPE threshold: handle both old (number) and new (object) formats
        const ppeOverride = config.confidence_thresholds.ppe;
        const normalizedOverride = {
          ...config.confidence_thresholds,
          ppe: ppeOverride ? extractPPEConfidence(ppeOverride) : undefined,
        };
        setConfidenceThresholds(prev => ({
          ...prev,
          ...normalizedOverride,
        }));
      }

      // Merge vehicle proximity settings
      if (config.vehicleProximity) {
        setVehicleProximitySettings(prev => ({
          ...prev,
          ...config.vehicleProximity,
        }));
      }

      // Merge processing settings
      if (config.processing) {
        setProcessingSettings(prev => ({
          ...prev,
          ...config.processing,
        }));
      }

      // Load analytics with zone assignments
      if (config.analytics) {
        console.log('[LOAD ANALYTICS] From config.analytics:', {
          count: config.analytics.length,
          enabled: config.analytics.filter(a => a.enabled).map(a => a.id),
        });
        setAnalytics(config.analytics);
      } else if (config.enabledAnalytics) {
        // Migrate from old format
        console.log('[LOAD ANALYTICS] Migrating from old format:', {
          enabled: config.enabledAnalytics,
        });
        setAnalytics(
          ANALYTICS_OPTIONS.map(opt => ({
            id: opt.id,
            enabled: config.enabledAnalytics.includes(opt.id),
            zones: ['all'],
          }))
        );
      } else {
        console.log('[LOAD ANALYTICS] No existing analytics, using defaults');
      }

      // Load stored reference image
      if (config.referenceImage) {
        setReferenceImage(config.referenceImage);
      }

      // Load zones from config
      const savedZones = Array.isArray(config.zones) ? config.zones : null;
      const databaseZones = Array.isArray(availableZones) ? availableZones : [];

      if (savedZones !== null) {
        // Use saved zones (including empty array if user deleted all zones)
        setZones(savedZones);
      } else {
        // First load: use database zones
        setZones(databaseZones);
      }
    } else {
      // No detectionConfig, use database zones
      setZones(Array.isArray(availableZones) ? availableZones : []);
      setAnalytics(ANALYTICS_OPTIONS.map(opt => ({
        id: opt.id,
        enabled: false,
        zones: ['all'],
      })));
    }
  }, [camera?.id, id]);

  // Debug: Log dropdown render state
  useEffect(() => {
    console.log('📊 Dropdown render state:', {
      selectedProfileId,
      dropdownValue: selectedProfileId || '',
      availableProfiles: profiles.length,
      profileIds: profiles.map(p => ({ id: p.id, displayName: p.displayName })),
      selectedProfileInList: profiles.find(p => p.id === selectedProfileId),
      cameraDetectionProfileId: camera?.detectionProfileId,
      match: selectedProfileId === camera?.detectionProfileId,
    });
  }, [selectedProfileId, profiles, camera?.detectionProfileId]);

  // Connect to WebSocket on mount
  useEffect(() => {
    connect();
  }, [connect]);

  // Auto-request snapshot when camera loads and socket is connected
  useEffect(() => {
    if (camera && camera.aiDevice?.status === 'online' && connected && !hasRequestedSnapshot.current) {
      hasRequestedSnapshot.current = true;
      handleRefreshSnapshot();
    }
  }, [camera, connected]);

  // Listen for snapshot responses
  useEffect(() => {
    if (streamState?.snapshot) {
      setReferenceImage(streamState.snapshot);
      setIsLoadingSnapshot(false);
      setSnapshotError(null);
      setHasChanges(true);
    }
  }, [streamState?.snapshot]);

  // Update camera mutation
  const createZoneMutation = useMutation({
    mutationFn: ({ zoneData }) => zonesAPI.createZone(zoneData),
    onSuccess: (response, { localId }) => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'zones',
      });
      // Replace the temp local ID with the real DB UUID so ZoneSelector
      // immediately has the correct ID without requiring a page reload.
      const savedZone = response?.data;
      if (savedZone?.id && localId) {
        setZones(prev => prev.map(z =>
          z.id === localId ? { ...z, id: savedZone.id, dbId: savedZone.id } : z
        ));
        setAnalytics(prev => prev.map(a => ({
          ...a,
          zones: a.zones.map(zid => zid === localId ? savedZone.id : zid),
        })));
      }
    },
    onError: (error) => {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      console.error('Failed to create zone in database:', error);
      toast.error(`Zone save failed: ${msg}`);
    },
  });

  // Mutation for updating existing zone with polygon data
  const updateZoneMutation = useMutation({
    mutationFn: ({ zoneId, polygonData }) => zonesAPI.updateZone(zoneId, polygonData),
    onSuccess: () => {
      // Invalidate all zones queries to reflect the updated polygon in /zones page
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'zones';
        },
      });
      toast.success('Zone polygon saved to /zones page');
    },
    onError: (error) => {
      console.error('Failed to update zone in database:', error);
      toast.error('Zone polygon updated locally but failed to save to database');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => {
      console.log('💾 Saving camera config with:', {
        detectionProfileId: data.detectionProfileId,
        hasDetectionConfig: !!data.detectionConfig,
      });
      return camerasAPI.updateCamera(id, data);
    },
    onSuccess: (response) => {
      console.log('✅ Save successful, response data:', {
        detectionProfileId: response?.data?.detectionProfileId,
        hasDetectionProfile: !!response?.data?.detectionProfile,
      });
      toast.success('Camera configuration saved');
      queryClient.invalidateQueries({ queryKey: ['camera', id] });
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to save configuration');
    },
  });

  // Request a new snapshot
  const handleRefreshSnapshot = () => {
    if (camera?.aiDevice?.status !== 'online') {
      setSnapshotError('AI device is offline');
      return;
    }
    if (!connected) {
      connect();
      setSnapshotError('Connecting to server...');
      return;
    }
    setIsLoadingSnapshot(true);
    setSnapshotError(null);
    requestSnapshot(id, false);

    setTimeout(() => {
      if (isLoadingSnapshot) {
        setIsLoadingSnapshot(false);
        setSnapshotError('Snapshot request timed out');
      }
    }, 10000);
  };

  // Toggle analytics
  const toggleAnalytic = (analyticId) => {
    setAnalytics(prev => prev.map(a =>
      a.id === analyticId ? { ...a, enabled: !a.enabled } : a
    ));
    setHasChanges(true);
  };

  // Update analytic zones
  const updateAnalyticZones = (analyticId, newZones) => {
    setAnalytics(prev => prev.map(a =>
      a.id === analyticId ? { ...a, zones: newZones } : a
    ));
    setHasChanges(true);
  };

  // Zone drawing handlers
  const handleCanvasClick = useCallback((e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setCurrentPoints(prev => [...prev, { x, y }]);
  }, [isDrawing]);

  const handleCompleteZone = () => {
    if (currentPoints.length < 3) {
      toast.error('A zone needs at least 3 points');
      return;
    }

    if (!newZoneName.trim()) {
      toast.error('Please enter a zone name');
      return;
    }

    // Check if we're updating an existing zone or creating a new one
    const existingZone = zones.find(z => z.id === selectedZone);
    const isUpdatingExisting = !!existingZone;

    if (isUpdatingExisting) {
      // Update existing zone's polygon
      const zone = zones.find(z => z.id === selectedZone);
      setZones(prev => prev.map(z =>
        z.id === selectedZone
          ? { ...z, points: currentPoints }
          : z
      ));
      setCurrentPoints([]);
      setNewZoneName('');
      setSelectedZone(null);
      setIsDrawing(false);
      setHasChanges(true);

      // Save polygon to database for existing zone
      if (zone?.dbId) {
        const coordinates = currentPoints.map(p => [p.x, p.y]);
        const polygonData = {
          polygon: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
        };
        updateZoneMutation.mutate({ zoneId: zone.dbId, polygonData });
      } else {
        toast.success('Zone polygon updated locally');
      }
    } else {
      // Create new zone
      const newZone = {
        id: `zone_${Date.now()}`,
        name: newZoneName,
        type: selectedZoneType,
        points: currentPoints,
        alertOnEntry: true,
      };

      setZones(prev => [...prev, newZone]);
      setCurrentPoints([]);
      setNewZoneName('');
      setIsDrawing(false);
      setHasChanges(true);
      toast.success('Zone added');

      // Also create the zone in the database so it appears in /zones page
      if (camera?.siteId) {
        const zonePayload = {
          siteId: camera.siteId,
          name: newZoneName,
          code: `${newZoneName.toUpperCase().replace(/\s+/g, '_')}_${Date.now()}`.substring(0, 50),
          type: selectedZoneType,
          polygon: {
            type: 'polygon',
            coordinates: [currentPoints.map(p => [p.x, p.y])],
          },
          alertOnEntry: true,
          alertOnPPE: true,
          alertOnOvercrowd: true,
          color: ZONE_TYPES.find(t => t.id === selectedZoneType)?.color || '#3B82F6',
        };

        createZoneMutation.mutate({ zoneData: zonePayload, localId: newZone.id });
      }
    }
  };

  const handleCancelDrawing = () => {
    setCurrentPoints([]);
    setIsDrawing(false);
  };

  // Check if a zone has a polygon (points) or polygon data
  const zoneHasPolygon = (zone) => {
    if (zone?.points && zone.points.length > 0) return true;
    if (zone?.polygon?.coordinates && zone.polygon.coordinates[0]?.length > 0) return true;
    return false;
  };

  // Convert polygon coordinates to points format
  const polygonToPoints = (polygon) => {
    if (!polygon?.coordinates || polygon.coordinates.length === 0) return [];
    const coords = polygon.coordinates[0] || []; // Get the first ring (outer boundary)
    return coords.map(([x, y]) => ({ x, y }));
  };

  // Handle drawing polygon for existing zone
  const handleDrawPolygonForZone = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      setSelectedZone(zoneId);
      setIsDrawing(true);
      // Use points if available, otherwise convert polygon to points
      const points = zone.points && zone.points.length > 0
        ? zone.points
        : polygonToPoints(zone.polygon);
      setCurrentPoints(points);
      setNewZoneName(zone.name);
      setSelectedZoneType(zone.type);
    }
  };

  // Handle zone assignment from modal (existing or new)
  const handleZoneAssigned = (zone) => {
    if (zone.source === 'existing') {
      // Zone selected from existing zones - convert polygon to points if needed
      let points = zone.points && zone.points.length > 0
        ? zone.points
        : polygonToPoints(zone.polygon);

      const newZone = {
        id: `zone_${Date.now()}`,
        ...zone,
        points, // Use converted points
        dbId: zone.dbId || zone.id,
      };
      setZones(prev => [...prev, newZone]);
      setHasChanges(true);
    } else if (zone.source === 'new') {
      // New zone created via modal drawing - add locally and create in DB
      setZones(prev => [...prev, zone]);
      setHasChanges(true);

      // Create the zone in database so it appears in /zones page
      if (camera?.siteId && zone.points && zone.points.length >= 3) {
        const coordinates = zone.points.map(p => [p.x, p.y]);
        const zonePayload = {
          siteId: camera.siteId,
          name: zone.name,
          code: `${zone.name.toUpperCase().replace(/\s+/g, '_')}_${Date.now()}`.substring(0, 50),
          type: zone.type,
          polygon: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          alertOnEntry: zone.alertOnEntry || true,
          alertOnPPE: true,
          alertOnOvercrowd: true,
          color: ZONE_TYPES.find(t => t.id === zone.type)?.color || '#3B82F6',
          isActive: true,
        };

        createZoneMutation.mutate({ zoneData: zonePayload, localId: zone.id });
      } else if (camera?.siteId) {
        toast.error('Cannot save zone: insufficient points for polygon');
      }
    }
  };

  const handleDeleteZone = (zoneId) => {
    setZones(prev => prev.filter(z => z.id !== zoneId));
    // Also remove zone from analytics that reference it
    setAnalytics(prev => prev.map(a => ({
      ...a,
      zones: a.zones.filter(z => z !== zoneId).length > 0
        ? a.zones.filter(z => z !== zoneId)
        : ['all']
    })));
    setSelectedZone(null);
    setHasChanges(true);
    toast.success('Zone deleted');
  };

  // Update confidence threshold
  const updateConfidence = (key, value) => {
    setConfidenceThresholds(prev => ({
      ...prev,
      [key]: parseFloat(value),
    }));
    setHasChanges(true);
  };

  // Reset confidence thresholds to defaults
  const resetConfidenceThresholds = () => {
    setConfidenceThresholds(DEFAULT_CONFIDENCE_THRESHOLDS);
    setHasChanges(true);
  };

  // Update vehicle proximity setting
  const updateVehicleProximity = (key, value) => {
    setVehicleProximitySettings(prev => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  // Update processing setting
  const updateProcessing = (key, value) => {
    setProcessingSettings(prev => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  // Save configuration
  const handleSave = () => {
    console.log('💾 handleSave called, current state:', {
      selectedProfileId,
      selectedProfileIdType: typeof selectedProfileId,
      willSend: selectedProfileId || null,
    });

    // Preserve nested PPE object (per-item thresholds) if present; only use flat number as fallback
    const normalizedThresholds = {
      ...confidenceThresholds,
      ppe: confidenceThresholds.ppe ?? 0.6,
    };

    const detectionConfig = {
      analytics,
      zones,
      confidence_thresholds: normalizedThresholds,
      vehicleProximity: vehicleProximitySettings,
      processing: processingSettings,
      referenceImage,
      updatedAt: new Date().toISOString(),
    };

    // Enhanced debug logging (matching mobile app)
    console.log('[CONFIG SAVE DEBUG] Sending config:', {
      cameraId: id,
      configData: detectionConfig,
      analyticsCount: detectionConfig.analytics?.length,
      analyticsEnabled: detectionConfig.analytics?.filter(a => a.enabled)?.map(a => a.id),
    });

    console.log('[CONFIG SAVE DEBUG] Payload being sent:', JSON.stringify({
      detectionConfig,
      detectionProfileId: selectedProfileId || null,
    }, null, 2));

    console.log('💾 About to call updateMutation with:', {
      detectionProfileId: selectedProfileId || null,
      hasDetectionConfig: !!detectionConfig,
      analyticsCount: detectionConfig.analytics.length,
      analyticsEnabled: detectionConfig.analytics.filter(a => a.enabled).map(a => a.id),
    });

    updateMutation.mutate({
      detectionConfig,
      detectionProfileId: selectedProfileId || null,
    });
  };

  // Draw zones on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw existing zones
    zones.forEach(zone => {
      const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
      const color = zoneType?.color || '#3B82F6';

      // Handle both local zones (with points) and database zones (with polygon)
      const points = zone.points || [];
      if (points.length === 0) return; // Skip zones without points

      ctx.beginPath();
      points.forEach((point, i) => {
        const x = (point.x / 100) * canvas.width;
        const y = (point.y / 100) * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();

      ctx.fillStyle = `${color}33`;
      ctx.fill();

      ctx.strokeStyle = selectedZone === zone.id ? '#FFFFFF' : color;
      ctx.lineWidth = selectedZone === zone.id ? 3 : 2;
      ctx.stroke();

      // Draw zone name
      if (points.length > 0) {
        const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
        const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
        const x = (centerX / 100) * canvas.width;
        const y = (centerY / 100) * canvas.height;

        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'center';
        ctx.fillText(zone.name, x, y);
      }
    });

    // Draw current drawing points
    if (currentPoints.length > 0) {
      const zoneType = ZONE_TYPES.find(t => t.id === selectedZoneType);
      const color = zoneType?.color || '#3B82F6';

      ctx.beginPath();
      currentPoints.forEach((point, i) => {
        const x = (point.x / 100) * canvas.width;
        const y = (point.y / 100) * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);

        // Draw point markers
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [zones, currentPoints, selectedZone, selectedZoneType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!camera) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Camera not found</p>
      </div>
    );
  }

  const enabledCount = analytics.filter(a => a.enabled).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/cameras')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configure Camera</h1>
            <p className="text-gray-500">{camera.name} - {camera.location || 'No location'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="btn btn-primary flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <CheckIcon className="w-4 h-4" />
            )}
            Save Configuration
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Camera Preview */}
        <div className="lg:col-span-2">
          <div className="card overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Camera Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefreshSnapshot}
                  className="btn btn-secondary btn-sm flex items-center gap-1"
                  disabled={isLoadingSnapshot || camera.aiDevice?.status !== 'online'}
                >
                  {isLoadingSnapshot ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4" />
                      Refresh Snapshot
                    </>
                  )}
                </button>
              </div>
            </div>

            <div
              ref={containerRef}
              className="relative aspect-video bg-gray-900"
            >
              {/* Reference image display */}
              {referenceImage ? (
                <img
                  src={referenceImage}
                  alt="Camera reference"
                  className="w-full h-full object-contain"
                />
              ) : isLoadingSnapshot ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p>Loading camera snapshot...</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <CameraIcon className="w-16 h-16 mx-auto mb-2" />
                    {snapshotError ? (
                      <>
                        <p className="text-red-400">{snapshotError}</p>
                        <p className="text-sm mt-1">Click "Refresh Snapshot" when device is online</p>
                      </>
                    ) : camera.aiDevice?.status === 'online' ? (
                      <p>Fetching camera snapshot...</p>
                    ) : (
                      <>
                        <p>No camera preview available</p>
                        <p className="text-sm mt-1">AI device is offline</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Zone drawing canvas overlay */}
              <canvas
                ref={canvasRef}
                className={clsx(
                  'absolute inset-0 w-full h-full',
                  isDrawing ? 'z-50 cursor-crosshair pointer-events-auto' : 'pointer-events-none'
                )}
                onClick={handleCanvasClick}
              />

              {/* Drawing mode indicator */}
              {isDrawing && (
                <div className="absolute top-2 left-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Drawing Zone - Click to add points
                </div>
              )}

              {/* Reference image info badge */}
              {referenceImage && !isDrawing && (
                <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                  Reference Image
                </div>
              )}
            </div>

            {/* Zone drawing controls - always visible */}
            <div className="p-4 bg-gray-50 border-t">
              {!isDrawing ? (
                <button
                  onClick={() => setShowZoneAssignmentModal(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  Assign/Create Zone
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newZoneName}
                    onChange={(e) => setNewZoneName(e.target.value)}
                    placeholder="Zone name..."
                    className="input flex-1"
                  />
                  <span className="text-sm text-gray-500">
                    {currentPoints.length} points
                  </span>
                  <button
                    onClick={handleCompleteZone}
                    disabled={currentPoints.length < 3}
                    className="btn btn-success flex items-center gap-2"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Complete
                  </button>
                  <button
                    onClick={handleCancelDrawing}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Unified Configuration Panel */}
        <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Zone Management Section */}
          <div className="card">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPinIcon className="w-5 h-5" />
                Zones ({zones.length})
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Define detection areas on the camera view
              </p>
            </div>
            {zones.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <MapPinIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No zones defined</p>
                <p className="text-xs">Use "Draw New Zone" below the preview</p>
              </div>
            ) : (
              <div className="divide-y">
                {zones.map(zone => {
                  const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
                  return (
                    <div
                      key={zone.id}
                      className={clsx(
                        'p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50',
                        selectedZone === zone.id && 'bg-blue-50'
                      )}
                      onClick={() => setSelectedZone(selectedZone === zone.id ? null : zone.id)}
                    >
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: zoneType?.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{zone.name}</p>
                        <p className="text-xs text-gray-500">{zoneType?.name}</p>
                      </div>
                      {!zoneHasPolygon(zone) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDrawPolygonForZone(zone.id);
                          }}
                          className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                          title="Draw polygon for this zone"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteZone(zone.id);
                        }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detection Profile Selector */}
          <div className="card bg-blue-50 border-l-4 border-l-blue-500">
            <div className="p-4">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                🎯 Detection Profile (Base Configuration)
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Select a profile as your base settings. You can customize individual thresholds below if needed.
              </p>
              <select
                value={selectedProfileId || ''}
                onChange={(e) => {
                  const profileId = e.target.value || null;
                  console.log('🎯 DROPDOWN onChange fired:', {
                    rawValue: e.target.value,
                    profileId,
                    previousSelectedProfileId: selectedProfileId,
                    availableProfiles: profiles.map(p => p.id),
                  });
                  console.log('📌 Before setSelectedProfileId - state is:', selectedProfileId);
                  setSelectedProfileId(profileId);
                  console.log('📌 After setSelectedProfileId - will be:', profileId);
                  setHasChanges(true);

                  if (profileId) {
                    const profile = profiles.find(p => p.id === profileId);
                    console.log('📋 Found profile:', profile);
                    if (profile?.config) {
                      setConfidenceThresholds(profile.config.confidence_thresholds || DEFAULT_CONFIDENCE_THRESHOLDS);
                      setVehicleProximitySettings(prev => ({
                        ...prev,
                        ...(profile.config.vehicleProximity || {})
                      }));
                      setProcessingSettings(prev => ({
                        ...prev,
                        ...(profile.config.processing || {})
                      }));
                      toast.success(`Loaded profile: ${profile.displayName}`);
                    }
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">-- None (Use Custom Settings) --</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.displayName} ({p.industry})
                    {p.isDefault && ' (Default)'}
                  </option>
                ))}
              </select>
              {selectedProfileId && (
                <p className="text-xs text-blue-600 mt-2">
                  ℹ️ Profile selected. Adjust thresholds below to customize for this camera.
                </p>
              )}
            </div>
          </div>

          {/* Confidence Thresholds Section */}
          <div className="card">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <AdjustmentsHorizontalIcon className="w-5 h-5" />
                  Detection Confidence
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Adjust thresholds to reduce false positives
                </p>
              </div>
              <button
                onClick={resetConfidenceThresholds}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Reset to Defaults
              </button>
            </div>
            <div className="p-4 space-y-5">
              {/* Person Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Person Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round(confidenceThresholds.person * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.05"
                  value={confidenceThresholds.person}
                  onChange={(e) => updateConfidence('person', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              {/* PPE Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    PPE Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {typeof confidenceThresholds.ppe === 'number' ? Math.round(confidenceThresholds.ppe * 100) : '60'}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.4"
                  max="0.9"
                  step="0.05"
                  value={typeof confidenceThresholds.ppe === 'number' ? confidenceThresholds.ppe : 0.6}
                  onChange={(e) => updateConfidence('ppe', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              {/* Fire/Smoke Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Fire/Smoke Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round(confidenceThresholds.fire * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.05"
                  value={confidenceThresholds.fire}
                  onChange={(e) => updateConfidence('fire', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              {/* Vehicle Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Vehicle Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round(confidenceThresholds.vehicle * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="0.95"
                  step="0.05"
                  value={confidenceThresholds.vehicle}
                  onChange={(e) => updateConfidence('vehicle', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500">
                  Lower thresholds detect more objects but may increase false positives.
                  Higher thresholds are more accurate but may miss some detections.
                </p>
              </div>
            </div>
          </div>

          {/* Processing Settings Section */}
          <div className="card">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <BoltIcon className="w-5 h-5" />
                Processing Settings
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Control processing rate and priority
              </p>
            </div>
            <div className="p-4 space-y-5">
              {/* Target FPS */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <ClockIcon className="w-4 h-4" />
                    Target FPS
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {processingSettings.targetFps} fps
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="1"
                  value={processingSettings.targetFps}
                  onChange={(e) => updateProcessing('targetFps', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1 fps (Low resource)</span>
                  <span>15 fps (High detail)</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Higher FPS means more frames analyzed per second, using more GPU/CPU resources.
                  For 50 cameras on RTX 3060, recommend 1-2 fps per camera.
                </p>
              </div>

              {/* Priority */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Processing Priority
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PRIORITY_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      onClick={() => updateProcessing('priority', option.id)}
                      className={clsx(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        processingSettings.priority === option.id
                          ? option.color === 'red' ? 'border-red-500 bg-red-50'
                          : option.color === 'orange' ? 'border-orange-500 bg-orange-50'
                          : option.color === 'blue' ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-500 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <p className={clsx(
                        'font-medium text-sm',
                        processingSettings.priority === option.id
                          ? option.color === 'red' ? 'text-red-700'
                          : option.color === 'orange' ? 'text-orange-700'
                          : option.color === 'blue' ? 'text-blue-700'
                          : 'text-gray-700'
                          : 'text-gray-700'
                      )}>
                        {option.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500">
                  Critical cameras are processed first and maintain frame rate during high load.
                  Low priority cameras may have reduced FPS when system is busy.
                </p>
              </div>
            </div>
          </div>

          {/* Analytics Section */}
          <div className="card">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5" />
                Detection Analytics
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {enabledCount} of {ANALYTICS_OPTIONS.length} enabled
              </p>
            </div>
            <div className="divide-y">
              {ANALYTICS_OPTIONS.map(option => {
                const analyticState = analytics.find(a => a.id === option.id);
                const isEnabled = analyticState?.enabled || false;
                const Icon = option.icon;

                return (
                  <div key={option.id} className="p-4">
                    <div
                      className="flex items-start gap-3 cursor-pointer"
                      onClick={() => toggleAnalytic(option.id)}
                    >
                      <div className={clsx(
                        'p-2 rounded-lg mt-0.5',
                        isEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                      )}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{option.name}</p>
                          <span className={clsx(
                            'text-xs px-1.5 py-0.5 rounded',
                            option.severity === 'CRITICAL' && 'bg-red-100 text-red-700',
                            option.severity === 'HIGH' && 'bg-orange-100 text-orange-700',
                            option.severity === 'MEDIUM' && 'bg-yellow-100 text-yellow-700',
                            option.severity === 'LOW' && 'bg-blue-100 text-blue-700',
                          )}>
                            {option.severity}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                      </div>
                      <div className={clsx(
                        'w-10 h-6 rounded-full transition-colors flex-shrink-0',
                        isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      )}>
                        <div className={clsx(
                          'w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5',
                          isEnabled ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                        )} />
                      </div>
                    </div>

                    {/* Zone selector - only shown when enabled */}
                    {isEnabled && (
                      <div className="mt-3 ml-11 flex items-center gap-2">
                        <span className="text-sm text-gray-500">Apply to:</span>
                        <ZoneSelector
                          zones={availableZones}
                          selectedZones={analyticState?.zones || ['all']}
                          onChange={(newZones) => updateAnalyticZones(option.id, newZones)}
                          disabled={option.requiresZones && availableZones.length === 0}
                        />
                        {option.requiresZones && zones.length === 0 && (
                          <span className="text-xs text-orange-500">Requires zones</span>
                        )}
                      </div>
                    )}

                    {/* Vehicle Proximity Distance Settings */}
                    {isEnabled && option.id === 'vehicle_proximity' && (
                      <div className="mt-4 ml-11 p-3 bg-gray-50 rounded-lg space-y-3">
                        <p className="text-sm font-medium text-gray-700">Distance Thresholds</p>

                        {/* Critical Distance */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-red-600 font-medium">
                              Critical (Immediate Danger)
                            </label>
                            <span className="text-xs font-mono bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                              &lt; {vehicleProximitySettings.criticalDistance}m
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="5"
                            step="0.5"
                            value={vehicleProximitySettings.criticalDistance}
                            onChange={(e) => updateVehicleProximity('criticalDistance', parseFloat(e.target.value))}
                            className="w-full h-1.5 bg-red-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                          />
                        </div>

                        {/* High Distance */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-orange-600 font-medium">
                              High (Dangerous Proximity)
                            </label>
                            <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                              &lt; {vehicleProximitySettings.highDistance}m
                            </span>
                          </div>
                          <input
                            type="range"
                            min="2"
                            max="10"
                            step="0.5"
                            value={vehicleProximitySettings.highDistance}
                            onChange={(e) => updateVehicleProximity('highDistance', parseFloat(e.target.value))}
                            className="w-full h-1.5 bg-orange-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                          />
                        </div>

                        {/* Medium Distance */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-yellow-600 font-medium">
                              Medium (Warning Zone)
                            </label>
                            <span className="text-xs font-mono bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">
                              &lt; {vehicleProximitySettings.mediumDistance}m
                            </span>
                          </div>
                          <input
                            type="range"
                            min="5"
                            max="20"
                            step="1"
                            value={vehicleProximitySettings.mediumDistance}
                            onChange={(e) => updateVehicleProximity('mediumDistance', parseFloat(e.target.value))}
                            className="w-full h-1.5 bg-yellow-200 rounded-lg appearance-none cursor-pointer accent-yellow-600"
                          />
                        </div>

                        <p className="text-xs text-gray-500 pt-1 border-t">
                          Alerts are triggered when a person is within these distances of a vehicle.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Camera Info */}
          <div className="card p-4">
            <h3 className="font-semibold mb-3">Camera Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={clsx(
                  'font-medium',
                  camera.status === 'online' ? 'text-green-600' : 'text-gray-400'
                )}>
                  {camera.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">AI Device</span>
                <span className="font-medium">
                  {camera.aiDevice?.name || 'Not assigned'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Device Status</span>
                <span className={clsx(
                  'font-medium',
                  camera.aiDevice?.status === 'online' ? 'text-green-600' : 'text-gray-400'
                )}>
                  {camera.aiDevice?.status || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zone Assignment Modal */}
      {showZoneAssignmentModal && (
        <ZoneAssignmentModal
          isOpen={showZoneAssignmentModal}
          onClose={() => {
            setShowZoneAssignmentModal(false);
            setIsDrawing(false);
            setCurrentPoints([]);
            setNewZoneName('');
            setSelectedZoneType('restricted');
          }}
          cameraId={camera?.id}
          siteId={camera?.siteId}
          existingZoneIds={zones.map(z => z.dbId || z.id).filter(Boolean)}
          onZoneAssigned={(zone) => {
            handleZoneAssigned(zone);
            setShowZoneAssignmentModal(false);
          }}
          currentPoints={currentPoints}
          setCurrentPoints={setCurrentPoints}
          onDrawingModeChange={handleDrawingModeChange}
        />
      )}
    </div>
  );
}
